// =============================================================================
// SimSense Platform — Auth Store (Zustand)
// =============================================================================
// Manages authentication state for the SimSense platform.
// Uses custom JWT + Google OAuth flows under /api/ss/auth/.
// =============================================================================

import { create } from "zustand";
import { api, uploadFormData } from "../utils/api";

const DEV_BYPASS = false; // Set true to bypass login check locally

export type UserTier = "free" | "pro" | "admin" | "beta_1" | "beta_2";

export interface AuthUser {
  userId: string;
  email: string;
  displayName: string | null;
  plan: UserTier;
  profilePictureUrl: string | null;
  bio: string | null;
  location: string | null;
  twitchUrl: string | null;
  youtubeUrl: string | null;
  twitterUrl: string | null;
  discordUsername: string | null;
  iracingId: string | null;
  iracingLinked: boolean;
  publicProfile: boolean;
  emailVerified: boolean;
  googleLinked: boolean;
  onboardingCompleted: boolean;
  createdAt: string;
  capabilities: {
    has_pro: boolean;
    has_coaching: boolean;
    is_admin: boolean;
  };
  preferences?: {
    shareSetups: boolean;
    shareTelemetry: boolean;
    units: "metric" | "standard";
    autoDeleteIbt: boolean;
    retentionMonths: number;
  };
}

export function parsePreferences(raw: unknown): AuthUser["preferences"] | undefined {
  if (!raw) return undefined;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (typeof raw === "object") {
    return raw as AuthUser["preferences"];
  }
  return undefined;
}

export function mapAuthUser(data: any): AuthUser {
  return {
    userId: data.user_id,
    email: data.email,
    displayName: data.display_name,
    plan: data.plan as UserTier,
    profilePictureUrl: data.profile_picture_url,
    bio: data.bio,
    location: data.location,
    twitchUrl: data.twitch_url ?? null,
    youtubeUrl: data.youtube_url ?? null,
    twitterUrl: data.twitter_url ?? null,
    discordUsername: data.discord_username ?? null,
    iracingId: data.iracing_id,
    iracingLinked: data.iracing_linked ?? false,
    publicProfile: data.public_profile ?? true,
    emailVerified: data.email_verified,
    googleLinked: data.google_linked,
    onboardingCompleted: data.onboarding_completed,
    createdAt: data.created_at,
    capabilities: data.capabilities || { has_pro: false, has_coaching: false, is_admin: false },
    preferences: parsePreferences(data.preferences) ?? {
      shareSetups: true,
      shareTelemetry: true,
      units: "standard",
      autoDeleteIbt: false,
      retentionMonths: 6,
    },
  };
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  _checked: boolean;

  checkAuth: (force?: boolean) => Promise<void>;
  login: (req: any) => Promise<void>;
  register: (req: any) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (req: any) => Promise<void>;
  uploadAvatar: (file: File) => Promise<AuthUser>;
  updatePreferences: (req: any) => Promise<void>;
  completeOnboarding: (req: any) => Promise<void>;
  
  // Legacy / Hybrid helpers
  loginWithGoogle: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: DEV_BYPASS
    ? {
        userId: "dev",
        email: "dev@simsense.gg",
        displayName: "SimSense Developer",
        plan: "admin",
        profilePictureUrl: null,
        bio: "Building the next generation of sim racing intelligence.",
        location: "SimWorld",
        twitchUrl: null,
        youtubeUrl: null,
        twitterUrl: null,
        discordUsername: null,
        iracingId: "12345",
        iracingLinked: true,
        publicProfile: true,
        emailVerified: true,
        googleLinked: false,
        onboardingCompleted: true,
        createdAt: new Date().toISOString(),
        capabilities: {
          has_pro: true,
          has_coaching: true,
          is_admin: true
        },
        preferences: { 
          shareSetups: true, 
          shareTelemetry: true, 
          units: "standard",
          autoDeleteIbt: false,
          retentionMonths: 6
        }
      }
    : null,
  loading: !DEV_BYPASS,
  error: null,
  _checked: DEV_BYPASS,

  checkAuth: async (force = false) => {
    if (get()._checked && !force) return;
    set({ loading: true, error: null });
    try {
      const data = await api<any>("/api/ss/auth/me");
      localStorage.setItem("ss_auth_hint", "1");
      set({ user: mapAuthUser(data), loading: false, _checked: true });
    } catch (err: any) {
      if (err.status === 401) {
        try {
          await api("/api/ss/auth/refresh", { method: "POST" });
          const data = await api<any>("/api/ss/auth/me");
          localStorage.setItem("ss_auth_hint", "1");
          set({ user: mapAuthUser(data), loading: false, _checked: true });
          return;
        } catch (refreshErr) {
          localStorage.removeItem("ss_auth_hint");
          set({ user: null, loading: false, _checked: true });
        }
      } else {
        console.error("Auth check failed", err);
        set({ error: err.message, user: null, loading: false, _checked: true });
      }
    }
  },

  register: async (req) => {
    set({ loading: true, error: null });
    try {
      await api("/api/ss/auth/register", {
        method: "POST",
        body: JSON.stringify(req),
      });
      const data = await api<any>("/api/ss/auth/me");
      localStorage.setItem("ss_auth_hint", "1");
      set({ user: mapAuthUser(data), loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  login: async (req) => {
    set({ loading: true, error: null });
    try {
      await api("/api/ss/auth/login", {
        method: "POST",
        body: JSON.stringify(req),
      });
      const data = await api<any>("/api/ss/auth/me");
      localStorage.setItem("ss_auth_hint", "1");
      set({ user: mapAuthUser(data), loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  loginWithGoogle: () => {
    window.location.href = "/api/ss/auth/google";
  },

  logout: async () => {
    localStorage.removeItem("ss_auth_hint");
    try {
      await api("/api/ss/auth/logout", { method: "POST" });
      set({ user: null });
      window.location.href = "/login";
    } catch (err) {
      console.error("Logout failed", err);
      set({ user: null });
      window.location.href = "/login";
    }
  },

  updateProfile: async (req) => {
    try {
      const data = await api<any>("/api/ss/auth/me", {
        method: "PATCH",
        body: JSON.stringify(req),
      });
      set((state) => ({
        user: state.user ? { 
          ...state.user, 
          displayName: data.display_name,
          bio: data.bio,
          location: data.location,
          twitchUrl: data.twitch_url ?? null,
          youtubeUrl: data.youtube_url ?? null,
          twitterUrl: data.twitter_url ?? null,
          discordUsername: data.discord_username ?? null,
          iracingId: data.iracing_id,
          publicProfile: data.public_profile ?? state.user.publicProfile,
          profilePictureUrl: data.profile_picture_url,
          capabilities: data.capabilities || state.user.capabilities
        } : null,
      }));
    } catch (err) {
      console.error("Failed to update profile", err);
      throw err;
    }
  },

  uploadAvatar: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const data = await uploadFormData<any>("/api/ss/auth/me/avatar", formData);
      const mapped = mapAuthUser(data);
      localStorage.setItem("ss_auth_hint", "1");
      set({ user: mapped, _checked: true });
      return mapped;
    } catch (err) {
      console.error("Failed to upload avatar", err);
      throw err;
    }
  },

  updatePreferences: async (req) => {
    try {
      await api("/api/ss/auth/me", {
        method: "PATCH",
        body: JSON.stringify({ preferences: JSON.stringify(req) }),
      });
      set((state) => ({
        user: state.user ? { 
          ...state.user, 
          preferences: { ...state.user.preferences, ...req } as any
        } : null,
      }));
    } catch (err) {
      console.error("Failed to update preferences", err);
      throw err;
    }
  },

  completeOnboarding: async (req) => {
    try {
      await api("/api/ss/auth/onboarding/complete", {
        method: "POST",
        body: JSON.stringify({ preferences: req }),
      });
      set((state) => ({
        user: state.user ? { 
          ...state.user, 
          onboardingCompleted: true,
          preferences: { ...state.user.preferences, ...req } as any
        } : null,
      }));
    } catch (err) {
      console.error("Failed to complete onboarding", err);
      throw err;
    }
  },
}));
